import { ref, type Ref, type InjectionKey, computed, type MaybeRefOrGetter, toValue, watch } from 'vue';
import lodashCloneDeep from 'lodash.clonedeep';
import lodashSet from 'lodash.set';
import lodashGet from 'lodash.get';
import { useErrors } from './StateManager.ts';

export const FormContextInjectionKey = Symbol('FormContext') as InjectionKey<FormContextInterface>;
export type InputFilesReader = () => File | File[] | null | undefined;

export function createFormContext(
  initialData: MaybeRefOrGetter<Record<string, any>>,
  initialBag: MaybeRefOrGetter<string>,
  initialReadonly: MaybeRefOrGetter<boolean>,
) {
  const bags = useErrors();

  const data = ref(lodashCloneDeep(toValue(initialData)));
  const files = ref({});
  const readonly = ref(toValue(initialReadonly));
  const bag = ref(toValue(initialBag));

  const touched = ref({}) as Ref<Record<string, boolean>>;
  const processing = ref(false);

  const errors = computed(() => bags.value[bag.value] || {});

  function touch(name: string) {
    lodashSet(touched.value, name, true);
  }

  function value(name: string, value: any) {
    return lodashGet(data.value, name, value);
  }

  function fill(name: string, value: any, filesReader: InputFilesReader | undefined = undefined) {
    if (filesReader) {
      lodashSet(files.value, name, filesReader());
    }

    lodashSet(data.value, name, value);
  }

  watch(() => toValue(initialData), (value) => data.value = lodashCloneDeep(value));
  watch(() => toValue(initialBag), (value) => bag.value = value);
  watch(() => toValue(initialReadonly), (value) => readonly.value = value);

  return {
    data,
    files,
    errors,
    touched,
    processing,
    readonly,
    touch,
    value,
    fill,
  };
}

export type FormContextInterface = ReturnType<typeof createFormContext>;

export function setModelWithContext(name: Nullable<string>, ctx: Nullable<FormContextInterface>, value: any): any;
export function setModelWithContext(name: Nullable<string>, ctx: Nullable<FormContextInterface>, value: any, input: HTMLInputElement | null | undefined): any;
export function setModelWithContext(name: Nullable<string>, ctx: Nullable<FormContextInterface>, value: any, input: HTMLInputElement | null | undefined = undefined) {
  if (name && ctx) {
    ctx.touch(name);
    ctx.fill(name, value, fileReaderFactory(input));
  }

  return value;
}

export function getModelFromContext(name: Nullable<string>, ctx: Nullable<FormContextInterface>, value: any) {
  if (name && ctx) {
    return ctx.value(name, value);
  }

  return value;
}

function fileReaderFactory(input: HTMLInputElement | null | undefined): InputFilesReader | undefined {
  if (input && (input.type === 'file' || input.type === 'image')) {
    return () => {
      if (input) {
        if (input.files instanceof FileList) {
          if (input.multiple) {
            return Array.from(input.files);
          } else {
            return input.files.item(0);
          }
        }
      }
    };
  }

  return undefined;
}
